import { cloneElement, createContext, useContext, useMemo, useState, type ComponentProps, type ReactElement, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type ActionProps = ComponentProps<'button'> & { 'data-testid'?: string }
type ActionDock = {
  target: HTMLDivElement | null
  register: (target: HTMLDivElement | null) => void
}
const ActionTarget = createContext<ActionDock | null>(null)

export function ReachableActions({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null)
  const dock = useMemo(() => enabled ? { target, register: setTarget } : null, [enabled, target])
  return (
    <div className="flex h-full flex-col">
      <ActionTarget.Provider value={dock}>{children}</ActionTarget.Provider>
    </div>
  )
}

export function ReachableActionSlot() {
  const dock = useContext(ActionTarget)
  if (!dock) return null
  return (
    <div
      ref={dock.register}
      role="group"
      aria-label="Quick conversation actions"
      data-testid="reachable-actions"
      className="hidden shrink-0 items-center gap-0.5 empty:hidden [@media(min-width:560px)_and_(any-pointer:coarse)]:flex"
    />
  )
}

export function ReachableAction({ children, enabled = true }: {
  children: ReactElement<ActionProps, 'button'>
  enabled?: boolean
}) {
  const target = useContext(ActionTarget)?.target
  if (!target || !enabled) return <>{children}</>
  const shortcut = cloneElement(children, {
    ref: null,
    id: undefined,
    'data-testid': undefined,
    className: 'inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg p-1 text-xs font-semibold text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-wait disabled:opacity-50 dark:text-stone-200',
    onPointerDown(event) {
      children.props.onPointerDown?.(event)
      if (event.pointerType === 'touch') event.preventDefault()
    },
    children: (
      <span className="inline-flex min-h-8 items-center gap-1 rounded-lg border border-stone-300 bg-white px-2 dark:border-stone-700 dark:bg-stone-800">
        {children.props.children}
      </span>
    ),
  })
  return <>{children}{createPortal(shortcut, target)}</>
}
